import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Search, X, ChevronDown, Check } from 'lucide-react';
import { useEstateHierarchy, useUserTypes, useUserEstateStatuses, type AudienceFilter } from '../../hooks/usePushAudience';
import { useDeviceTokenUsers, type DeviceTokenRecord } from '../../hooks/useDeviceTokens';

// This is a new file — per the SAM-175 BaseUI/Styletron freeze
// (SAM-175), new code may not import baseui or
// styletron-react outside src/components/ui/. There's no ui/ Combobox yet
// (that's still planned per the RFC's §4), so the multi-select below is
// hand-rolled with Tailwind + tokens.css, matching the dropdown pattern the
// legacy PushNotifications.tsx already used for its manual user search.

interface Option {
  id: number;
  label: string;
}

// --- Field shell -----------------------------------------------------------

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div className="mb-4">
    <label className="mb-1.5 block text-[13px] font-semibold text-ink-700">{label}</label>
    {children}
    {hint && <div className="mt-1 text-xs text-ink-400">{hint}</div>}
  </div>
);

// --- Hand-rolled searchable multi-select ------------------------------------

interface MultiSelectFieldProps {
  options: Option[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  placeholder?: string;
  emptyPlaceholder?: string;
  disabled?: boolean;
  loading?: boolean;
  searchable?: boolean;
}

const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  options,
  selectedIds,
  onChange,
  placeholder = 'Select…',
  emptyPlaceholder = 'Nothing to select yet',
  disabled = false,
  loading = false,
  searchable = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  const filteredOptions = useMemo(() => {
    if (!searchable || !query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, query, searchable]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedLabels = useMemo(
    () => options.filter(o => selectedSet.has(o.id)).map(o => o.label),
    [options, selectedSet],
  );

  const toggle = (id: number) => {
    onChange(selectedSet.has(id) ? selectedIds.filter(i => i !== id) : [...selectedIds, id]);
  };

  const summary = selectedLabels.length === 0
    ? placeholder
    : selectedLabels.length <= 2
      ? selectedLabels.join(', ')
      : `${selectedLabels.length} selected`;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className={`flex w-full items-center justify-between gap-2 rounded-8 border border-line bg-white px-3 py-[7px] text-left text-[13px] transition-colors ${
          disabled ? 'cursor-not-allowed bg-surface-3 text-ink-400' : 'text-ink-900 hover:border-ink-300'
        }`}
      >
        <span className={`truncate ${selectedLabels.length === 0 ? 'text-ink-400' : ''}`}>
          {loading ? 'Loading…' : summary}
        </span>
        <ChevronDown size={14} className="shrink-0 text-ink-400" />
      </button>

      {open && !disabled && (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-[100001] max-h-[240px] overflow-auto rounded-8 border border-line bg-white shadow-2">
          {searchable && (
            <div className="sticky top-0 border-b border-line-subtle bg-white p-2">
              <div className="flex items-center gap-1.5 rounded-6 border border-line px-2 py-1">
                <Search size={13} className="text-ink-400" />
                <input
                  autoFocus
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Filter…"
                  className="w-full border-none bg-transparent text-[13px] text-ink-700 outline-none"
                />
              </div>
            </div>
          )}

          {filteredOptions.length === 0 ? (
            <div className="p-3 text-center text-[13px] text-ink-400">{options.length === 0 ? emptyPlaceholder : 'No matches'}</div>
          ) : (
            filteredOptions.map(opt => {
              const checked = selectedSet.has(opt.id);
              return (
                <div
                  key={opt.id}
                  onClick={() => toggle(opt.id)}
                  className="flex cursor-pointer items-center gap-2 px-3 py-2 text-[13px] text-ink-900 hover:bg-line-subtle"
                >
                  <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border ${checked ? 'border-tenant bg-tenant' : 'border-ink-300'}`}>
                    {checked && <Check size={11} className="text-white" />}
                  </span>
                  <span className="truncate">{opt.label}</span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// --- Chips -------------------------------------------------------------------

const Chip: React.FC<{ label: string; className?: string; onRemove: () => void }> = ({ label, className = '', onRemove }) => (
  <span className={`inline-flex items-center gap-1 rounded-6 px-2.5 py-1 text-xs font-medium ${className}`}>
    {label}
    <button type="button" onClick={onRemove} className="opacity-70 hover:opacity-100">
      <X size={11} />
    </button>
  </span>
);

const ChipGroup: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-2.5">
    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-400">{title}</div>
    <div className="flex flex-wrap gap-1.5">{children}</div>
  </div>
);

// --- Public types --------------------------------------------------------------

export interface SelectedUser {
  id: number;
  name: string;
  email: string;
}

interface PushAudienceBuilderProps {
  filter: AudienceFilter;
  onFilterChange: (filter: AudienceFilter) => void;
  selectedUsers: SelectedUser[];
  onSelectedUsersChange: (users: SelectedUser[]) => void;
}

export const PushAudienceBuilder: React.FC<PushAudienceBuilderProps> = ({
  filter,
  onFilterChange,
  selectedUsers,
  onSelectedUsersChange,
}) => {
  const { isLoading: hierarchyLoading, estateGroupOptions, buildingsByGroup, allEstates } = useEstateHierarchy();
  const { userTypes, isLoading: userTypesLoading } = useUserTypes();
  const { statuses, isLoading: statusesLoading } = useUserEstateStatuses();
  const { uniqueUsers, isLoading: devicesLoading } = useDeviceTokenUsers();

  const [userSearch, setUserSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // --- Cascading options ---

  const buildingOptions: Option[] = useMemo(() => {
    if (filter.estateGroupIds.length === 0) return [];
    const seen = new Set<number>();
    const opts: Option[] = [];
    for (const groupId of filter.estateGroupIds) {
      for (const b of buildingsByGroup.get(groupId) || []) {
        if (!seen.has(b.id)) { seen.add(b.id); opts.push(b); }
      }
    }
    return opts;
  }, [filter.estateGroupIds, buildingsByGroup]);

  const estateOptions: Option[] = useMemo(() => {
    if (filter.estateGroupIds.length === 0) return [];
    const groupSet = new Set(filter.estateGroupIds);
    const buildingSet = new Set(filter.buildingIds);
    return allEstates
      .filter(e => {
        if (e.estate_group_id == null || !groupSet.has(e.estate_group_id)) return false;
        if (buildingSet.size > 0 && (e.building_id == null || !buildingSet.has(e.building_id))) return false;
        return true;
      })
      .map(e => ({ id: e.id, label: e.name }));
  }, [filter.estateGroupIds, filter.buildingIds, allEstates]);

  const userTypeOptions: Option[] = useMemo(
    () => (userTypes || []).map((t: { id: number; name?: string }) => ({ id: t.id, label: t.name || `Type ${t.id}` })),
    [userTypes],
  );

  // Ticket only asks for approved/pending; keep to those two so we don't
  // surface internal-only statuses (rejected/expired/etc.) as send filters.
  const statusOptions: Option[] = useMemo(() => {
    const relevant = (statuses || []).filter((s: { name?: string }) => /approved|pending/i.test(s.name || ''));
    if (relevant.length > 0) {
      return relevant.map((s: { id: number; name: string }) => ({ id: s.id, label: s.name }));
    }
    // Fallback to the well-known ids used elsewhere (AddUserEstateDrawer) if
    // the status list hasn't loaded yet or names don't match.
    return [{ id: 1, label: 'Pending' }, { id: 2, label: 'Approved' }];
  }, [statuses]);

  // --- Change handlers ---

  const updateFilter = useCallback((patch: Partial<AudienceFilter>) => {
    onFilterChange({ ...filter, ...patch });
  }, [filter, onFilterChange]);

  const handleGroupChange = (estateGroupIds: number[]) => {
    // Reset dependent levels when the group selection changes so stale
    // buildings/estates from a de-selected group don't linger.
    updateFilter({ estateGroupIds, buildingIds: [], estateIds: [] });
  };

  const removeEstateGroup = (id: number) => updateFilter({
    estateGroupIds: filter.estateGroupIds.filter(g => g !== id),
    buildingIds: [],
    estateIds: [],
  });
  const removeBuilding = (id: number) => updateFilter({ buildingIds: filter.buildingIds.filter(b => b !== id), estateIds: [] });
  const removeEstate = (id: number) => updateFilter({ estateIds: filter.estateIds.filter(e => e !== id) });
  const removeUserType = (id: number) => updateFilter({ userTypeIds: filter.userTypeIds.filter(t => t !== id) });
  const removeStatus = (id: number) => updateFilter({ statusIds: filter.statusIds.filter(s => s !== id) });

  // --- Label lookups for chips ---

  const estateGroupLabelById = useMemo(() => new Map<number, string>(estateGroupOptions.map(o => [o.id, o.label])), [estateGroupOptions]);
  const buildingLabelById = useMemo(() => {
    const map = new Map<number, string>();
    for (const opts of buildingsByGroup.values()) for (const b of opts) map.set(b.id, b.label);
    return map;
  }, [buildingsByGroup]);
  const estateLabelById = useMemo(() => new Map<number, string>(allEstates.map(e => [e.id, e.name])), [allEstates]);
  const userTypeLabelById = useMemo(() => new Map<number, string>(userTypeOptions.map(o => [o.id, o.label])), [userTypeOptions]);
  const statusLabelById = useMemo(() => new Map<number, string>(statusOptions.map(o => [o.id, o.label])), [statusOptions]);

  // --- Manual individual user search (combines with the segment above) ---

  const searchResults = useMemo(() => {
    if (!userSearch.trim() || userSearch.length < 2) return [];
    const q = userSearch.toLowerCase();
    const selectedIds = new Set(selectedUsers.map(u => u.id));
    return uniqueUsers
      .filter((d: DeviceTokenRecord) => {
        if (selectedIds.has(d.user_id)) return false;
        const u = d.user;
        return u?.email?.toLowerCase().includes(q) || u?.name?.toLowerCase().includes(q) || u?.last_name?.toLowerCase().includes(q);
      })
      .slice(0, 8);
  }, [uniqueUsers, userSearch, selectedUsers]);

  const addUser = useCallback((device: DeviceTokenRecord) => {
    const u = device.user;
    const nextSelected = [...selectedUsers, {
      id: device.user_id,
      name: [u?.name, u?.last_name].filter(Boolean).join(' ') || `User #${device.user_id}`,
      email: u?.email || '',
    }];
    onSelectedUsersChange(nextSelected);
    updateFilter({ userIds: nextSelected.map(s => s.id) });
    setUserSearch('');
    setShowDropdown(false);
  }, [selectedUsers, onSelectedUsersChange, updateFilter]);

  const removeUser = useCallback((userId: number) => {
    const nextSelected = selectedUsers.filter(u => u.id !== userId);
    onSelectedUsersChange(nextSelected);
    updateFilter({ userIds: nextSelected.map(s => s.id) });
  }, [selectedUsers, onSelectedUsersChange, updateFilter]);

  const hasAnyChips =
    filter.estateGroupIds.length > 0 || filter.buildingIds.length > 0 || filter.estateIds.length > 0 ||
    filter.userTypeIds.length > 0 || filter.statusIds.length > 0 || selectedUsers.length > 0;

  return (
    <div>
      <Field label="Estate group" hint="Pick one or more groups — leave empty to not filter by group">
        <MultiSelectField
          options={estateGroupOptions}
          selectedIds={filter.estateGroupIds}
          onChange={handleGroupChange}
          placeholder="All estate groups"
          loading={hierarchyLoading}
          searchable
        />
      </Field>

      <Field
        label="Building"
        hint={filter.estateGroupIds.length > 0 ? 'Optional — filter estates by building' : 'Pick an estate group first'}
      >
        <MultiSelectField
          options={buildingOptions}
          selectedIds={filter.buildingIds}
          onChange={ids => updateFilter({ buildingIds: ids, estateIds: [] })}
          placeholder="All buildings"
          disabled={filter.estateGroupIds.length === 0}
          loading={hierarchyLoading}
          searchable
        />
      </Field>

      <Field
        label="Estate"
        hint={filter.estateGroupIds.length > 0 ? 'Optional — type to filter by name' : 'Pick an estate group first'}
      >
        <MultiSelectField
          options={estateOptions}
          selectedIds={filter.estateIds}
          onChange={ids => updateFilter({ estateIds: ids })}
          placeholder="All estates in scope"
          disabled={filter.estateGroupIds.length === 0}
          loading={hierarchyLoading}
          searchable
        />
      </Field>

      <Field label="User type" hint="Optional — resident, contractor, staff, etc.">
        <MultiSelectField
          options={userTypeOptions}
          selectedIds={filter.userTypeIds}
          onChange={ids => updateFilter({ userTypeIds: ids })}
          placeholder="All user types"
          loading={userTypesLoading}
        />
      </Field>

      <Field label="Estate status" hint="Optional — the user's approval status on the estate">
        <MultiSelectField
          options={statusOptions}
          selectedIds={filter.statusIds}
          onChange={ids => updateFilter({ statusIds: ids })}
          placeholder="Approved and pending"
          loading={statusesLoading}
        />
      </Field>

      <Field label="Individual users" hint="Optional — add specific people on top of the segment above">
        <div className="relative">
          <div className="flex items-center gap-1.5 rounded-8 border border-line px-3 py-[7px]">
            <Search size={14} className="text-ink-400" />
            <input
              value={userSearch}
              onChange={e => { setUserSearch(e.target.value); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
              placeholder="Search users by name or email..."
              disabled={devicesLoading}
              className="w-full border-none bg-transparent text-[13px] text-ink-900 outline-none placeholder:text-ink-400"
            />
          </div>

          {showDropdown && searchResults.length > 0 && (
            <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-10 max-h-[200px] overflow-auto rounded-8 border border-line bg-white shadow-2">
              {searchResults.map(device => {
                const u = device.user;
                const name = [u?.name, u?.last_name].filter(Boolean).join(' ') || `User #${device.user_id}`;
                return (
                  <div
                    key={device.user_id}
                    onMouseDown={() => addUser(device)}
                    className="cursor-pointer px-3 py-2 text-[13px] hover:bg-line-subtle"
                  >
                    <div className="font-medium text-ink-900">{name}</div>
                    <div className="text-[11px] text-ink-400">{u?.email || ''}</div>
                  </div>
                );
              })}
            </div>
          )}

          {showDropdown && userSearch.length >= 2 && searchResults.length === 0 && (
            <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-10 rounded-8 border border-line bg-white shadow-2">
              <div className="p-3 text-center text-[13px] text-ink-400">No users found</div>
            </div>
          )}
        </div>
      </Field>

      {hasAnyChips && (
        <div className="mb-4">
          {filter.estateGroupIds.length > 0 && (
            <ChipGroup title="Estate groups">
              {filter.estateGroupIds.map(id => (
                <Chip key={`eg-${id}`} className="bg-[#EEF2FF] text-[#3730A3]" label={estateGroupLabelById.get(id) || `#${id}`} onRemove={() => removeEstateGroup(id)} />
              ))}
            </ChipGroup>
          )}

          {filter.buildingIds.length > 0 && (
            <ChipGroup title="Buildings">
              {filter.buildingIds.map(id => (
                <Chip key={`b-${id}`} className="bg-[#DBEAFE] text-[#1E40AF]" label={buildingLabelById.get(id) || `#${id}`} onRemove={() => removeBuilding(id)} />
              ))}
            </ChipGroup>
          )}

          {filter.estateIds.length > 0 && (
            <ChipGroup title="Estates">
              {filter.estateIds.map(id => (
                <Chip key={`e-${id}`} className="bg-ok-bg text-ok-fg" label={estateLabelById.get(id) || `#${id}`} onRemove={() => removeEstate(id)} />
              ))}
            </ChipGroup>
          )}

          {(filter.userTypeIds.length > 0 || filter.statusIds.length > 0) && (
            <ChipGroup title="Refinements">
              {filter.userTypeIds.map(id => (
                <Chip key={`ut-${id}`} className="bg-warn-bg text-warn-fg" label={userTypeLabelById.get(id) || `#${id}`} onRemove={() => removeUserType(id)} />
              ))}
              {filter.statusIds.map(id => (
                <Chip key={`st-${id}`} className="bg-warn-bg text-warn-fg" label={statusLabelById.get(id) || `#${id}`} onRemove={() => removeStatus(id)} />
              ))}
            </ChipGroup>
          )}

          {selectedUsers.length > 0 && (
            <ChipGroup title="Individual users">
              {selectedUsers.map(u => (
                <Chip key={`u-${u.id}`} className="bg-tenant text-white" label={u.name} onRemove={() => removeUser(u.id)} />
              ))}
            </ChipGroup>
          )}
        </div>
      )}
    </div>
  );
};
